export type AgentErrorCode = "context_window_exceeded" | "unknown";

export class AgentServiceError extends Error {
  readonly code: AgentErrorCode;

  constructor(message: string, code: AgentErrorCode = "unknown") {
    super(message);
    this.name = "AgentServiceError";
    this.code = code;
  }
}

const CONTEXT_WINDOW_MARKERS = [
  "contextwindowexceeded",
  "context window exceeded",
  "maximum number of tokens allowed",
  "input token count exceeds",
  "context_window_exceeded",
];

export function isContextWindowExceededMessage(message: string): boolean {
  const lower = message.toLowerCase();
  return CONTEXT_WINDOW_MARKERS.some((marker) => lower.includes(marker));
}

const CONTEXT_WINDOW_USER_MESSAGE =
  "This conversation has grown too large for the model. Start a new chat to continue.";

function detailMessage(detail: unknown): string | null {
  if (typeof detail === "string") return detail;
  if (detail && typeof detail === "object" && "message" in detail) {
    const message = (detail as { message?: unknown }).message;
    return typeof message === "string" ? message : null;
  }
  return null;
}

function detailCode(detail: unknown): string | null {
  if (detail && typeof detail === "object" && "code" in detail) {
    const code = (detail as { code?: unknown }).code;
    return typeof code === "string" ? code : null;
  }
  return null;
}

/** Parse a failed agent API response into a typed AgentServiceError. */
export async function parseAgentServiceError(
  response: Response,
  fallbackPrefix: string,
): Promise<AgentServiceError> {
  let detail: unknown;
  try {
    const body = (await response.json()) as { detail?: unknown };
    detail = body.detail;
  } catch {
    return new AgentServiceError(`${fallbackPrefix}: ${response.statusText}`);
  }

  const code = detailCode(detail);
  const message = detailMessage(detail);

  if (code === "context_window_exceeded") {
    return new AgentServiceError(
      message ?? CONTEXT_WINDOW_USER_MESSAGE,
      "context_window_exceeded",
    );
  }

  if (message && isContextWindowExceededMessage(message)) {
    return new AgentServiceError(CONTEXT_WINDOW_USER_MESSAGE, "context_window_exceeded");
  }

  if (message) {
    return new AgentServiceError(message);
  }

  return new AgentServiceError(`${fallbackPrefix}: ${response.statusText}`);
}
